import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-hot-toast";
import {
  MessageSquare,
  FileText,
  CheckCircle2,
  ActivitySquare,
  BarChart3,
  IndianRupee
} from "lucide-react";
import "chart.js/auto";
import { Line } from "react-chartjs-2";

import useBilling from "../Services/BillingServices/useBilling";
import useWhatsapp from "../Services/WhatsappServices/useWhatsapp";
/**
 * ClientInsights (New Layout)
 * - Top insight cards (totalCost, totalMessages, avgCost, successRate, activeCampaigns)
 * - Templates list (click selects TemplateShow)
 * - Campaigns list
 * - Line chart for selected template performance (from getTemplateInsights)
 *
 * Endpoints (via GlobalContext.api):
 *  - POST /Whatsapp/getTemplates            { userid }
 *  - POST /Campaign/getCampaigns            { userid, campaignName }
 *  - POST /Admin/v1.0/getBillingData        { userid, interval, secret }
 *  - POST /Admin/v1.0/getTemplateInsights   { userid, templateId, secret }
 */

export default function ClientInsights() {
  const { getWhatsappTemplates, getBillingInsights, getTemplateInsights  } = useWhatsapp();
  const {getCampaignsSimple}= useBilling();
  const { api } = useContext(GlobalContext);
  const { clientId, clientName } = useParams();

  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [insights, setInsights] = useState({});
  const [templateName, setTemplateName] = useState("Select Template to Show Insights");
  const [selectedTemplateRow, setSelectedTemplateRow] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetchers
 const fetchTemplates = async () => {
  if (!clientId) return;

  try {
    const result =
      await getWhatsappTemplates(clientId);

    if (result.success) {
      setTemplates(result.data);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed to load templates");
  }
};

const fetchCampaigns = async () => {
  if (!clientId) return;

  try {
    const result =
      await getCampaignsSimple({
        userid: clientId,
        campaignName: "string",
      });

    if (result.success) {
      setCampaigns(result.data);
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Failed to load campaigns");
  }
};

const fetchInsightsSummary = async () => {
  if (!clientId) return;

  try {
    const result =
      await getBillingInsights({
        userid: clientId,
        interval: "30",
        secret: "string",
      });

    if (result.success) {
      setInsights(result.data);
    }
  } catch {
    // silent (same behavior as before)
  }
};

const fetchTemplateInsight = async (
  templateId
) => {
  if (!templateId) return;

  setLoading(true);

  try {
    const result =
      await getTemplateInsights({
        userid: clientId,
        templateId,
        secret: "string",
      });

    if (result.success) {
      const arr = result.data || [];
      setChartData(
        transformLineData(arr)
      );
    } else {
      setChartData(null);
    }
  } catch {
    setChartData(null);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchTemplates();
    fetchCampaigns();
    fetchInsightsSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // when templateName or insights change, map selected row
  useEffect(() => {
    if (!templateName || !insights?.campaignWise) return;
    const matched = (insights.campaignWise || []).find(
      (x) => x.campaignName === templateName
    );
    setSelectedTemplateRow(matched || null);
  }, [templateName, insights]);

  // Build chart data format
  const transformLineData = (arr) => {
    const labels = arr.map((d) =>
      new Date(d.start).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
    );
    return {
      labels,
      datasets: [
        { label: "Sent",       data: arr.map((d) => d.sent),       borderColor: "#ef4444", tension: 0.4 },
        { label: "Delivered",  data: arr.map((d) => d.delivered),  borderColor: "#7c3aed", tension: 0.4 },
        { label: "Read",       data: arr.map((d) => d.read),       borderColor: "#2563eb", tension: 0.4 },
        { label: "Replies",    data: arr.map((d) => d.conversation), borderColor: "#111827", tension: 0.4 },
      ],
    };
  };

  // UI helpers
  const statusBadge = (s) => {
    if (s === "APPROVED" || s === "Y") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (s === "N" || s === "REJECTED") return "bg-rose-50 text-rose-700 border-rose-200";
    return "bg-amber-50 text-amber-700 border-amber-200";
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <MessageSquare size={20} />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">
              {clientName}&apos;s WhatsApp Insight
            </div>
            <div className="text-xs text-gray-500">
              Template performance, costs & campaign status.
            </div>
          </div>
        </div>
      </div>

      {/* Top Insight Cards */}
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard title="Total Cost" value={insights?.totalCost} icon={<IndianRupee size={16} />} color="text-emerald-600" />
        <StatCard title="Total Messages" value={insights?.totalMessages} icon={<ActivitySquare size={16} />} color="text-blue-600" />
        <StatCard title="Average Cost" value={insights?.averageCost?.slice?.(0,5)} icon={<BarChart3 size={16} />} color="text-amber-600" />
        <StatCard title="Success Rate" value={insights?.successRate} icon={<CheckCircle2 size={16} />} color="text-rose-600" />
        <StatCard title="Active Campaigns" value={insights?.activeCampaigns} icon={<FileText size={16} />} color="text-indigo-600" />
      </div>

      {/* Main Body */}
      <div className="grid grid-cols-12 gap-3">
        {/* Left: Template detail + chart */}
        <div className="col-span-12 lg:col-span-6 space-y-3">
          <div className="rounded-xl border bg-white">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <div>
                <div className="text-sm font-semibold">
                  {templateName}
                </div>
                <div className="text-xs text-gray-500">
                  Message performance & cost breakdown.
                </div>
              </div>
              <button
                className="rounded-lg border px-3 py-1.5 text-xs"
                onClick={() =>
                  selectedTemplateRow?.templateId && fetchTemplateInsight(selectedTemplateRow.templateId)
                }
              >
                Refresh
              </button>
            </div>

            {/* small summary */}
            <div className="grid grid-cols-2 gap-2 p-3">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-gray-500">Amount Spent</div>
                <div className="text-xl font-bold">{selectedTemplateRow?.totalAmoutSpent ?? "—"}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-gray-500">Cost / Delivered</div>
                <div className="text-xl font-bold">{selectedTemplateRow?.costPerMessage ?? "—"}</div>
              </div>
            </div>

            {/* performance mini stats */}
            <div className="grid grid-cols-4 gap-2 px-3 pb-3">
              <MiniStat label="Sent" value={selectedTemplateRow?.totalSent} />
              <MiniStat label="Delivered" value={selectedTemplateRow?.totalDelivered} />
              <MiniStat label="Read" value={selectedTemplateRow?.totalRead} />
              <MiniStat label="Replies" value={selectedTemplateRow?.totalReplied} />
            </div>

            {/* Chart */}
            <div className="h-[340px] px-3 pb-3">
              {loading ? (
                <div className="grid h-full place-items-center text-gray-400 text-sm">Loading…</div>
              ) : chartData?.labels?.length ? (
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 6 } } },
                    scales: { y: { beginAtZero: true } },
                  }}
                />
              ) : (
                <div className="grid h-full place-items-center text-gray-400 text-sm">
                  Select a template from the list to view its chart.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Templates & Campaigns */}
        <div className="col-span-12 lg:col-span-6 space-y-3">
          {/* Templates */}
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <FileText size={16} className="text-[#01C37D]" /> Templates
            </div>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-2 py-2">Name</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr
                      key={t.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setTemplateName(t.name)}
                    >
                      <td className="px-2 py-2">{t.name}</td>
                      <td className="px-2 py-2">
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusBadge(
                            t.status
                          )}`}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td className="px-2 py-2">{(t.category || "").slice(0, 5)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {templates.length === 0 && (
                <div className="py-8 text-center text-gray-400">No templates.</div>
              )}
            </div>
          </div>

          {/* Campaigns */}
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <MessageSquare size={16} className="text-[#01C37D]" /> Campaigns
            </div>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-2 py-2">Name</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2 text-center">Audience</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2">{c.campaign_name}</td>
                      <td className="px-2 py-2">
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusBadge(
                            c.status
                          )}`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">{(c.members || []).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {campaigns.length === 0 && (
                <div className="py-8 text-center text-gray-400">No campaigns.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="flex items-center gap-2 text-xs text-gray-500">{icon}<span>{title}</span></div>
      <div className={`text-2xl font-bold ${color || "text-gray-900"}`}>{value ?? "—"}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border p-3 text-center">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-lg font-bold">{value ?? "—"}</div>
    </div>
  );
}
